
import * as d3 from 'd3';

import { Partitioner } from './Partitioner.js';

const STATES = {
    "alabama": "al",
    "alaska": "ak",
    "arizona": "az",
    "arkansas": "ar",
    "california": "ca",
    "colorado": "co",
    "connecticut": "ct",
    "delaware": "de",
    "district of columbia": "dc",
    "florida": "fl",
    "georgia": "ga",
    "hawaii": "hi",
    "idaho": "id",
    "illinois": "il",
    "indiana": "in",
    "iowa": "ia",
    "kansas": "ks",
    "kentucky": "ky",
    "louisiana": "la",
    "maine": "me",
    "maryland": "md",
    "massachusetts": "ma",
    "michigan": "mi",
    "minnesota": "mn",
    "mississippi": "ms",
    "missouri": "mo",
    "montana": "mt",
    "nebraska": "ne",
    "nevada": "nv",
    "new hampshire": "nh",
    "new jersey": "nj",
    "new mexico": "nm",
    "new york": "ny",
    "north carolina": "nc",
    "north dakota": "nd",
    "ohio": "oh",
    "oklahoma": "ok",
    "oregon": "or",
    "pennsylvania": "pa",
    "puerto rico": "pr",
    "rhode island": "ri",
    "south carolina": "sc",
    "south dakota": "sd",
    "tennessee": "tn",
    "texas": "tx",
    "utah": "ut",
    "vermont": "vt",
    "virginia": "va",
    "virgin islands": "vi",
    "washington": "wa",
    "west virginia": "wv",
    "wisconsin": "wi",
    "wyoming": "wy"
}

const COUNTRIES = {
    // North America
    'ca': 'canada',
    'us': 'united states',
    'mx': 'mexico',
    'bm': 'bermuda',
    
    // South America
    'ar': 'argentina',
    'br': 'brazil',
    'cl': 'chile',
    'py': 'paraguay',
    'uy': 'uruguay',

    // Europe
    'eu': '', 
    'al': 'albania', 
    'at': 'austria', 
    'by': 'belarus', 
    'be': 'belgium', 
    'ba': 'bosnia and herzegovina', 
    'bg': 'bulgaria', 
    'hr': 'croatia', 
    'cy': 'cyprus', 
    'cz': 'czechia', 
    'dk': 'denmark', 
    'ee': 'estonia',  
    'fi': 'finland', 
    'fr': 'france', 
    'ge': 'georgia', 
    'de': 'germany', 
    'gr': 'greece', 
    'hu': 'hungary', 
    'is': 'iceland',  
    'ie': 'ireland', 
    'it': 'italy', 
    'xk': 'kosovo', 
    'lv': 'latvia', 
    'lt': 'lithuania', 
    'lu': 'luxembourg', 
    'mk': 'macedonia', 
    'mt': 'malta', 
    'md': 'moldova', 
    'me': 'montenegro', 
    'nl': 'netherlands', 
    'no': 'norway', 
    'pl': 'poland', 
    'pt': 'portugal', 
    'ro': 'romania', 
    'ru': 'russia', 
    'rs': 'serbia', 
    'sk': 'slovakia', 
    'si': 'slovenia', 
    'es': 'spain', 
    'se': 'sweden', 
    'ch': 'switzerland', 
    'ua': 'ukraine', 
    'gb': 'united kingdom',

    // Middle East
    'il': 'israel',  
    'jo': 'jordan',
    'lb': 'lebanon',
    'sy': 'syria', 

    // Asia
    'am': 'armenia',
    'az': 'azerbaijan',
    'bd': 'bangladesh',
    'cn': 'china',
    'in': 'india',
    'id': 'indonesia',
    'kz': 'kazakhstan',
    'kg': 'kyrgyzstan',
    'tr': 'turkey',  
    'tm': 'turkmenistan',
    'jp': 'japan',

    // Africa
    'dz': 'algeria', 
    'ly': 'libya',
    'mg': 'madagascar',
    'ma': 'morocco',
    'za': 'south africa',
    'tn': 'tunisia', 

    // Oceania
    'au': 'australia',
    'nz': 'new zealand',
}

// Webpack chokes on Object.entries/Object.fromEntries when in a web worker ... ???????????????????????
const COUNTRIES_REVERSED = {};
Object.keys(COUNTRIES).forEach(iso => {
    COUNTRIES_REVERSED[COUNTRIES[iso]] = iso;
})

const REGIONS = {
    'new-england': ['me', 'nh', 'vt', 'ma', 'ct', 'ri'],
    'mid-atlantic': ['ny', 'nj', 'pa', 'md', 'de', 'dc', 'va', 'wv'],
    'southeast': ['nc', 'sc', 'ga', 'fl', 'al', 'ms', 'la', 'ar', 'tn', 'ky'],
    'midwest': ['oh', 'in', 'mi', 'il', 'wi', 'mn', 'ia', 'mo'],
    'plains': ['nd', 'sd', 'ne', 'ks', 'ok', 'tx'],
    'southwest': ['nm', 'co', 'az', 'ut', 'nv', 'ca'],
    'northwest': ['wa', 'or', 'id', 'mt', 'wy']
}

const VARIABLES = {
    'path-length': {'type': 'number', 'units': units => units == 'metric' ? 'km' : 'mi'}, 
    'path-width': {'type': 'number', 'units': units => units == 'metric' ? 'm' : 'yds'}, 
    'injuries': {'type': 'number', 'units': () => ''}, 
    'fatalities': {'type': 'number', 'units': () => ''}, 
    'num-states': {'type': 'number', 'units': () => ''},
    'state': {'type': 'enum', 'options': Object.values(STATES).concat(Object.keys(STATES))},
    'region': {'type': 'enum', 'options': Object.keys(REGIONS)},
    'country': {'type': 'enum', 'options': Object.values(COUNTRIES).concat(Object.keys(COUNTRIES))}
};

const VALID_OPERATORS = {
    'number': ['>', '>=', '=', '<=', '<'],
    'enum': ['='],
    'string': ['='],
};

const OPERATORS = (() => {
    let unique = [...new Set(Object.values(VALID_OPERATORS).flat())];
    unique.sort((a, b) => b.length - a.length);
    return unique;
})();

const DISPLAY_OPS = {
    '>': '>',
    '>=': '\u2265',
    '=': '=',
    '<=': '\u2264',
    '<': '<',
}

const OP_FUNCS = {
    '>': (a, b) => a > b,
    '>=': (a, b) => a >= b,
    '=': (a, b) => a == b,
    '<=': (a, b) => a <= b,
    '<': (a, b) => a < b,
}

const titleCase = str => str.replace(/(^|\s)\S/g, c => c.toUpperCase()).replace('And', 'and');

class Filter {
    constructor() {
        this.func = null;
    }

    filter(tors) {
        return tors.filter(this.func);
    }

    display() {
        return null;
    }

    toHeaderString() {
        throw "Implement in subclass!";
    }

    toRepr() {
        throw "Implement in subclass!";
    }

    static fromRepr(str) {
        const [filter_type, other] = str.split('|');
        const filter_types = {
            'DomainFilter': DomainFilter,
            'PartitionFilter': PartitionFilter,
            'PropertyFilter': PropertyFilter,
        }

        return filter_types[filter_type].fromRepr(str);
    }
}


class PartitionFilter extends Filter {
    constructor(partitioner, active) {
        super();

        this.part = partitioner;
        this.active = active === undefined ? this.part.part_keys : active;
        this.func = tor => this.active.includes(this.part.partition_func(tor));
    }

    togglePartition(part) {
        let active = this.active;

        if (active.includes(part)) {
            active = active.filter(el => el != part);
        }
        else {
            active.push(part);
        }

        return new PartitionFilter(this.part, active);
    }

    toHeaderString() {
        return this.part.to_header_string(this.active);
    }

    getPartDisplayStyle(key) {
        const idx = this.part.part_keys.indexOf(key);
        const bg_color = this.active.includes(key) ? this.part.colors[idx] : '#ffffff';
        const text_color = d3.hsl(bg_color).l > 0.5 ? '#000000' : '#ffffff';
        return {'backgroundColor': bg_color, 'color': text_color};
    }

    toRepr() {
        return 'PartitionFilter|' + this.part.toRepr() + '|' + this.active.join(',');
    }

    static fromRepr(str) {
        const [filter_type, partitioner_str, active] = str.split('|');
        if (filter_type != "PartitionFilter") {
            throw "Expected filter_type to be PartitionFilter, got '" + filter_type + "'";
        }
        const partitioner = Partitioner.fromRepr(partitioner_str);
        return new PartitionFilter(partitioner, active.split(','))
    }
}

class DomainFilter extends Filter {
    constructor(country_list) {
        super();

        if (country_list !== undefined) {
            this.country_list = new Set(country_list);

            this.func = tor => {
                const country_list = tor['geojson']['properties']['countries'] !== undefined ? tor['geojson']['properties']['countries'] : ['US'];
                return country_list.map(c => this.country_list.has(c.toLowerCase())).reduce((a, b) => a || b)
            };
        }
        else {
            this.func = () => true;
        }
    }

    toRepr() {
        const countries_str = this.country_list === undefined ? '' : [...this.country_list].join(',');
        return "DomainFilter|" + countries_str;
    }

    static fromRepr(str) {
        const [filter_type, country_list_str] = str.split('|');
        if (filter_type != "DomainFilter") {
            throw "Expected filter_type to be DomainFilter, got '" + filter_type + "'";
        }

        const country_list = country_list_str.length == 0 ? undefined : country_list_str.split(',');
        return new DomainFilter(country_list);
    }
}


class PropertyFilter extends Filter {
    constructor(variable, operator, value) {
        super();

        this.variable = variable;
        this.operator = operator;
        this.value = value;

        let op_func = OP_FUNCS[operator];
        if (variable == 'state') {
            op_func = (a, b) => a === undefined ? false : a.includes(b);
        }
        else if (variable == 'region') {
            op_func = (a, b) => a === undefined ? false : REGIONS[b].filter(el => a.includes(el.toUpperCase())).length > 0;
        }
        else if (variable == 'num-states') {
            const op_func_ = op_func;
            op_func = (a, b) => op_func_(a.length, b);
        }
        else if (variable == 'country') {
            op_func = (a, b) => a.includes(COUNTRIES_REVERSED[b.toLowerCase()].toUpperCase());
        }

        let db_variable = ['state', 'region', 'num-states'].includes(variable) ? 'states' : variable;
        db_variable = ['country'].includes(variable) ? 'countries' : db_variable;
        this.func = tor => op_func(tor['geojson']['properties'][db_variable], value);
    }  
    
    display(units_system) {
        let units = VARIABLES[this.variable]['type'] == 'number' ? VARIABLES[this.variable]['units'](units_system) : '';
        let value = this.value;
        if (units == 'm') {
            value *= 0.914402758; // Convert yards to meters
        }
        else if (units == 'km') {
            value *= 1.6093445; // Convert miles to kilometers
        }

        if (VARIABLES[this.variable]['type'] == 'number') {
            value = Math.round(value * 100) / 100;
        }
        units = units == '' ? units : ' ' + units;
        return this.variable + " " + DISPLAY_OPS[this.operator] + " " + value + units;
    }

    toHeaderString(units_system) {
        const var_str = this.variable.split('-').map(str => titleCase(str)).join(' ');
        const op_str = DISPLAY_OPS[this.operator];

        let val_str, units;
        if (VARIABLES[this.variable]['type'] == 'number') {
            units = VARIABLES[this.variable]['units'](units_system);
            val_str = this.value;
            if (units == 'm') {
                val_str *= 0.914402758; // Convert yards to meters
            }
            else if (units == 'km') {
                val_str *= 1.6093445; // Convert miles to kilometers
            }

            if (VARIABLES[this.variable]['type'] == 'number') {
                val_str = Math.round(val_str * 100) / 100;
            }
        }
        else {
            units = '';
            val_str = this.value.split('-').map(str => titleCase(str)).join(' ');
            val_str = val_str == 'Mid Atlantic' ? 'Mid-Atlantic': val_str;
        }
        units = units == '' ? units : ' ' + units;


        return var_str + ' ' + op_str + ' ' + val_str + units;
    }

    static fromString(filter_str, units_system, allow_states) {
        const parsed = PropertyFilter.getPossibilities(filter_str, units_system, allow_states);

        if (!parsed['maybe_complete']) {
            return null;
        }

        const variable = parsed['vars'];
        let value = parsed['val']['value'];

        if (variable == 'state') {
            if(value in STATES) {
                value = STATES[value];
            }
            value = value.toUpperCase();
        }
        else if (variable == 'country') {
            if (value in COUNTRIES) {
                value = COUNTRIES[value];
            }
            value = titleCase(value);
        }
        else if (variable == 'path-width' && units_system == 'metric') {
            value /= 0.914402758;
        }
        else if (variable == 'path-length' && units_system == 'metric') {
            value /= 1.6093445;
        }

        return new PropertyFilter(variable, parsed['ops'], value);
    }

    toRepr() {
        return 'PropertyFilter|' + this.variable + this.operator + this.value;
    }

    static fromRepr(str) {
        const [filter_type, filter_str] = str.split('|');
        if (filter_type != "PropertyFilter") {
            throw "Expected filter_type to be PropertyFilter, got '" + filter_type + "'";
        }

        return PropertyFilter.fromString(filter_str, 'imperial', true);
    }

    static getPossibilities(txt, units_system, allow_states) {
        let ret = {};
        const ops_in_txt = OPERATORS.filter(op => txt.includes(op));
        let var_txt, op_txt = null, val_txt = null;
        if (ops_in_txt.length > 0) {
            op_txt = ops_in_txt[0];
            [var_txt, val_txt] = txt.split(op_txt);
            var_txt = var_txt.trim().toLowerCase();
            val_txt = val_txt.trim().toLowerCase();
        }
        else {
            var_txt = txt.trim().toLowerCase();
        }

        let variables = {...VARIABLES};
        if (!allow_states) {
            delete variables['state'];
            delete variables['num-states'];
            delete variables['region'];
        }
      
        // It should be possible to generalize some stuff in here and make this a bit more tractible, but I don't have
        //  motivation for that right now.
        const valid_vars = Object.keys(variables);
        const cand_vars = valid_vars.filter(v => v.startsWith(var_txt));
        ret['vars'] = {'type': 'enum', 'options': cand_vars};
        ret['autocomplete'] = null;
        if (cand_vars.length == 1) {
            // If there's only one possible variable
            const var_name = cand_vars[0];
            if (valid_vars.includes(var_txt)) {
                // If there's only one possible variable, and it's been filled in, then start looking for operators.
                ret['vars'] = var_txt;
                let var_info = {...variables[var_name]};

                if (var_info['type'] == 'number') {
                    var_info['units'] = var_info['units'](units_system);
                }
      
                const valid_ops = VALID_OPERATORS[var_info['type']];
                if (op_txt !== null) {
                    // The user has entered an operator, so check to see that it's valid
                    if (valid_ops.includes(op_txt)) {
                        // The user has entered a valid operator, so check for the values
                        if (val_txt === null || val_txt === "") {
                            // The user hasn't entered a value yet, so there's a catch. Because the operands are short
                            //  and there's a lot of overlap in the characters, maybe they haven't finished entering it
                            //  yet (e.g., they're in the process of typing '>=', but have only finished '>' now). So
                            //  add an option for both operands and values
                            const cand_ops = valid_ops.filter(o => o.startsWith(op_txt));
                            if (cand_ops.length == 1) {
                                ret['ops'] = cand_ops[0];
                            }
                            else {
                                ret['ops'] = {'type': 'enum', 'options': cand_ops};
                            }
                            if (cand_ops.filter(o => o == op_txt).length > 0) {
                                ret['val'] = var_info;
                            }
                        }
                        else {
                            // The user has entered a value, so parse the values
                            ret['ops'] = op_txt;
                            ret['val'] = var_info;

                            if (var_info['type'] == 'enum') {
                                // The value type is an enumeration, so pare down the options
                                const cand_vals = var_info['options'].filter(v => v.startsWith(val_txt));
                                if (cand_vals.length == 1 && cand_vals[0] != val_txt) {
                                    ret['autocomplete'] = txt.toLowerCase().replace(val_txt, cand_vals[0]);
                                }
                                ret['val']['options'] = cand_vals;

                                if (cand_vals.includes(val_txt)) {
                                    ret['val']['value'] = val_txt;
                                }
                            }
                            else {
                                // The value is either a number or a string, so stick the entered value in the object.
                                ret['val']['value'] = val_txt;
                            }
                        }
                    }
                    else {
                        // The user has entered an operator, but not one that's valid for this variable (e.g., entering
                        //  'state>'). No options.
                        ret['ops'] = {'type': 'enum', 'options': []};
                    }   
                }
                else {
                    // The user hasn't entered an operator yet, so give them all the valid choices for this variable.
                    ret['ops'] = {'type': 'enum', 'options': valid_ops};
                    if (valid_ops.length == 1) {
                        ret['autocomplete'] = txt + valid_ops[0];
                    }
                }
            }
            else {
                // If there's only one possible variable, but it hasn't been filled yet, allow autocomplete.
                ret['autocomplete'] = var_name;
            }
        }
      
        ret['maybe_complete'] = (typeof ret['vars'] !== 'object' && typeof ret['ops'] !== 'object' && 'value' in ret['val']);
        return ret;
    }
}

export { Filter, PropertyFilter, PartitionFilter, DomainFilter };